import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { useAuthStore } from "@/modules/auth";
import type { HolidaySetRef } from "./holidays";
import type { ReversalPayrollState } from "./reversal";

export type PayScheme = "hourly" | "fixed_days";

/** "" = global percent fallback; percent = % of MTD accrual; amount = absolute cap. */
export type AdvanceBasis = "" | "percent" | "amount";

export interface EmployeeRow {
  id: string;
  userId: string;
  empId: string;
  fullName: string;
  email: string;
  role: string;
  payScheme: PayScheme;
  committedDays: number | null;
  overtimeEligible: boolean;
  advanceBasis: AdvanceBasis;
  advanceCapValue: string | null;
  holidaySets: HolidaySetRef[];
  /** Slip branding profile — null falls back to the default profile. */
  brandingProfile: { id: string; name: string; status: "active" | "inactive" } | null;
  joinDate: string;
  exitDate: string | null;
  pan: string;
  dob: string | null;
  address: string;
  bankAccount: string;
  ifsc: string;
  bankName: string;
  currentBaseSalary: string | null;
  primaryLocation: {
    id: string;
    name: string;
    shiftFrom: string | null;
    shiftTo: string | null;
  } | null;
  status: "active" | "inactive";
  createdAt: string | null;
  updatedAt: string | null;
}

export interface EmployeeListResponse {
  results: EmployeeRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface EmployeeCreateInput {
  userId: string;
  baseSalary: number;
  payScheme: PayScheme;
  committedDays?: number | null;
  overtimeEligible: boolean;
  advanceBasis?: AdvanceBasis;
  advanceCapValue?: number | null;
  holidaySetIds: string[];
  joinDate?: string | null;
  exitDate?: string | null;
  pan?: string;
  dob?: string | null;
  address?: string;
  bankAccount?: string;
  ifsc?: string;
  bankName?: string;
}

export type EmployeeUpdateInput = Partial<Omit<EmployeeCreateInput, "userId" | "baseSalary">>;

export interface SalaryRevisionDoc {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  docNo: string | null;
}

export interface SalaryRevisionRow {
  id: string;
  amount: string;
  effectiveFrom: string;
  createdAt: string | null;
  document: SalaryRevisionDoc | null;
}

export interface PayrollSettingRow {
  key: string;
  value: unknown;
  editable: boolean;
}

export interface PayrollSettingsResponse {
  settings: PayrollSettingRow[];
}

export function usePayrollEmployees(
  params: {
    status?: "active" | "inactive" | "all" | "";
    search?: string;
    payScheme?: PayScheme | "";
  } = {},
) {
  return useQuery<EmployeeListResponse>({
    queryKey: ["payrollEmployees", "list", params],
    queryFn: () =>
      api
        .get<EmployeeListResponse>("/payroll/employees", {
          params: {
            status: params.status || undefined,
            search: params.search || undefined,
            payScheme: params.payScheme || undefined,
          },
        })
        .then((r) => r.data),
  });
}

export function usePayrollEmployee(id: string | undefined) {
  return useQuery<EmployeeRow>({
    queryKey: ["payrollEmployees", "detail", id],
    queryFn: () => api.get<EmployeeRow>(`/payroll/employees/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreatePayrollEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeCreateInput) =>
      api.post<EmployeeRow>("/payroll/employees", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useUpdatePayrollEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeUpdateInput) =>
      api.patch<EmployeeRow>(`/payroll/employees/${id}`, input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useDeactivatePayrollEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<EmployeeRow>(`/payroll/employees/${id}/deactivate`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useActivatePayrollEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<EmployeeRow>(`/payroll/employees/${id}/activate`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useSalaryRevisions(employeeId: string | undefined) {
  return useQuery<SalaryRevisionRow[]>({
    queryKey: ["payrollEmployees", employeeId ?? "_", "revisions"],
    queryFn: () =>
      api
        .get<SalaryRevisionRow[]>(`/payroll/employees/${employeeId}/revisions`)
        .then((r) => r.data),
    enabled: !!employeeId,
  });
}

export function useReviseSalary(employeeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { amount: number; effectiveFrom: string; attachmentId?: string }) =>
      api
        .post<SalaryRevisionRow>(`/payroll/employees/${employeeId}/revisions`, input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

export function useSetRevisionDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { revisionId: string; attachmentId: string }) =>
      api
        .post<SalaryRevisionRow>(`/payroll/revisions/${input.revisionId}/document`, {
          attachmentId: input.attachmentId,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollEmployees"] }),
  });
}

/* -------------------------------- runs -------------------------------- */

export type RunStatus = "draft" | "review" | "finalized";

export interface RunIssue {
  employeeId: string;
  name: string;
  reason: string;
}

export interface PayrollRunRow {
  id: string;
  month: string; // ISO date, first of month
  status: RunStatus;
  computedAt: string | null;
  submittedAt: string | null;
  finalizedAt: string | null;
  reopenedAt: string | null;
  reopenReason: string;
  issues: RunIssue[];
  lineCount: number;
  totalGross: string;
  totalNet: string;
  disbursedCount: number;
}

export interface PayrollLineRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  payScheme: PayScheme;
  baseSalary: string;
  totalFractions: string;
  targetUnits: string;
  otUnits: string;
  workedHours: string;
  requiredHours: string;
  otHours: string;
  hourlyRate: string;
  sundayCredits: number;
  holidayCredits: number;
  presentDayUnits: string;
  /** Working-day status counts (hourly scheme only; null for fixed_days). */
  dayCounts: {
    fullPunchDays: number;
    noMarkoutDays: number;
    leaveDays: number;
    absentDays: number;
  } | null;
  /** Pay components expressed as equivalent hours; totalHours × ratePerHour =
   *  basic earned. Hourly scheme with a known rate only; null otherwise. */
  hoursBreakup: {
    ratePerHour: string;
    components: {
      key: "regular" | "overtime" | "halfDay" | "sunday" | "holiday";
      label: string;
      hours: string;
      amount: string;
    }[];
    totalHours: string;
    totalAmount: string;
  } | null;
  basePay: string;
  otPay: string;
  sundayPay: string;
  holidayPay: string;
  voucherTotal: string;
  advanceDeduction: string;
  carryForwardDeduction: string;
  reimbursementTotal: string;
  gross: string;
  net: string;
  paidAmount: string;
  remainingAmount: string;
  fullyPaid: boolean;
  // Paid beyond net — non-zero when a draft-stage payment outgrew the final
  // computed net (warn-allow-flag; finalize never blocks).
  overpaidAmount: string;
}

export interface CalcSnapshotDay {
  date: string;
  isSunday: boolean;
  isHoliday: boolean;
  leave: string | null;
  expectedHours: string;
  workedHours: string;
  fraction: string;
  targetUnits: string;
  note: string;
  /** Punch times as "HH:MM" local, or null (no punch / legacy snapshot). */
  inTime: string | null;
  outTime: string | null;
}

export interface CalcSnapshot {
  payScheme: PayScheme;
  daysInMonth: number;
  totalFractions: string;
  targetUnits: string;
  otUnits: string;
  workedHours: string;
  requiredHours: string;
  otHours: string;
  hourlyRate: string;
  dailyWage: string;
  shiftHours: string;
  numWorkingDays: number;
  standardMonthlyHours: string;
  sundayCredits: number;
  holidayCredits: number;
  creditedSundays: string[];
  creditedHolidays: string[];
  presentDayUnits: string;
  committedDays: number | null;
  days: CalcSnapshotDay[];
}

export interface PayrollLineBreakdown extends PayrollLineRow {
  calcSnapshot: CalcSnapshot;
}

export interface EarningsEstimate {
  asOf: string;
  month: string;
  earnedSoFar: string;
  payScheme: PayScheme;
  /** HR run-lines display metrics — rate settings (multipliers/ratios) never ship here. */
  components: {
    hourlyRate: string;
    workedHours: string;
    requiredHours: string;
    otHours: string;
    paidSundays: number;
    paidHolidays: number;
    daysWorked: string;
  };
  basic: {
    baseSalary: string | null;
    effectiveFrom: string | null;
    dailyWage: string;
  };
  breakdown?: CalcSnapshot;
}

export function usePayrollRuns() {
  return useQuery<PayrollRunRow[]>({
    queryKey: ["payrollRuns", "list"],
    queryFn: () => api.get<PayrollRunRow[]>("/payroll/runs").then((r) => r.data),
  });
}

export function usePayrollRun(id: string | undefined) {
  return useQuery<PayrollRunRow>({
    queryKey: ["payrollRuns", "detail", id],
    queryFn: () => api.get<PayrollRunRow>(`/payroll/runs/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function usePayrollRunLines(runId: string | undefined) {
  return useQuery<PayrollLineRow[]>({
    queryKey: ["payrollRuns", runId ?? "_", "lines"],
    queryFn: () =>
      api.get<PayrollLineRow[]>(`/payroll/runs/${runId}/lines`).then((r) => r.data),
    enabled: !!runId,
  });
}

export function useComputeRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { year: number; month: number }) =>
      api.post<PayrollRunRow>("/payroll/runs", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

function runAction(action: string) {
  return (runId: string, body: object = {}) =>
    api.post<PayrollRunRow>(`/payroll/runs/${runId}/${action}`, body).then((r) => r.data);
}

export function useRunAction(action: "submit-review" | "return-to-draft" | "finalize") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => runAction(action)(runId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useReopenRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { runId: string; reason: string }) =>
      runAction("reopen")(input.runId, { reason: input.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useLineBreakdown(lineId: string | undefined) {
  return useQuery<PayrollLineBreakdown>({
    queryKey: ["payrollLines", lineId ?? "_", "breakdown"],
    queryFn: () =>
      api.get<PayrollLineBreakdown>(`/payroll/lines/${lineId}/breakdown`).then((r) => r.data),
    enabled: !!lineId,
  });
}

export function useMyEarningsEstimate(enabled = true) {
  return useQuery<EarningsEstimate>({
    queryKey: ["payroll", "myEstimate"],
    queryFn: () => api.get<EarningsEstimate>("/payroll/me/estimate").then((r) => r.data),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Lump-sum salary summary for one month. `source: "run"` = numbers from a
 *  computed payroll line (draft numbers included, marked provisional);
 *  `source: "estimate"` = live engine numbers, additions/deductions unknown. */
export interface MyMonthSummary {
  month: string; // YYYY-MM
  joinDate: string | null;
  source: "run" | "estimate";
  runStatus: RunStatus | null;
  provisional: boolean;
  asOf: string | null;
  baseSalary: string | null;
  gross: string;
  basePay: string;
  otPay: string;
  sundayPay: string;
  holidayPay: string;
  voucherTotal: string | null;
  reimbursementTotal: string | null;
  advanceDeduction: string | null;
  carryForwardDeduction: string | null;
  net: string | null;
  components: {
    workedHours: string;
    requiredHours: string;
    otHours: string;
    paidSundays: number;
    paidHolidays: number;
    daysWorked: string;
  };
}

export function useMyMonthSummary(month: string, enabled = true) {
  return useQuery<MyMonthSummary>({
    queryKey: ["payroll", "myMonthSummary", month],
    queryFn: () =>
      api
        .get<MyMonthSummary>("/payroll/me/month-summary", { params: { month } })
        .then((r) => r.data),
    enabled: enabled && !!month,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/* -------------------------------- slips -------------------------------- */

export type SlipStatus = "valid" | "superseded" | "void";

export interface PayslipRow {
  id: string;
  slipNo: string;
  period: string; // ISO date, first of month
  net: string;
  version: number;
  status: SlipStatus;
  attachmentId: string;
  issuedAt: string | null;
  replacedBy: string | null;
  paidOn: string | null;
  paidAmount: string | null;
  /** Lump-sum component totals from the payroll line — null once a reopen
   *  deletes the line (older reissued months). */
  earnings: {
    basePay: string;
    otPay: string;
    sundayPay: string;
    holidayPay: string;
    gross: string;
    voucherTotal: string;
    reimbursementTotal: string;
    advanceDeduction: string;
    carryForwardDeduction: string;
  } | null;
  /** Own-breakdown link — set only while the issuing run stays finalized. */
  lineId: string | null;
}

export function useMySlips() {
  return useQuery<PayslipRow[]>({
    queryKey: ["payroll", "mySlips"],
    queryFn: () => api.get<PayslipRow[]>("/payroll/me/slips").then((r) => r.data),
  });
}

export function useRunSlips(runId: string | undefined, enabled = true) {
  return useQuery<(PayslipRow & { employeeName: string; empId: string })[]>({
    queryKey: ["payrollRuns", runId ?? "_", "slips"],
    queryFn: () =>
      api
        .get<(PayslipRow & { employeeName: string; empId: string })[]>(
          `/payroll/runs/${runId}/slips`,
        )
        .then((r) => r.data),
    enabled: enabled && !!runId,
  });
}

/* ------------------------------- advances ------------------------------- */

export type AdvanceStatus =
  | "pending_hr"
  | "pending_final"
  | "active"
  | "closed"
  | "rejected"
  | "cancelled";

export interface AdvanceLedgerEntry {
  id: string;
  kind: "payroll" | "manual";
  amount: string;
  runMonth: string | null;
  reference: string;
  note: string;
  recordedAt: string | null;
  balanceAfter: string;
}

export interface AdvanceRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  amount: string;
  reason: string;
  balance: string;
  requestedInstallments: number;
  installmentsTotal: number;
  monthsRecovered: number;
  monthlyDue: string | null;
  accruedAtRequest: string;
  status: AdvanceStatus;
  hrApprovedAt: string | null;
  finalApprovedAt: string | null;
  settlementRef: string;
  settlementNote: string;
  /** Blank on advances approved before the pool feature (2026-09-04). */
  settlementMode: "bank" | "cash" | "upi" | "cheque" | "";
  settlementPoolId: string | null;
  settlementPoolMonth: string | null;
  resolutionNote: string;
  createdAt: string | null;
  ledger?: AdvanceLedgerEntry[];
}

export interface AdvanceEligibility {
  tenureOk: boolean;
  minTenureYears: number;
  basis: "percent" | "amount";
  capValue: string;
  capLabel: string;
  earnedSoFar: string;
  maxAmount: string;
  maxInstallments: number;
  hasOpenAdvance: boolean;
}

export interface MyAdvancesResponse {
  advances: AdvanceRow[];
  eligibility: AdvanceEligibility | null;
}

export function useAdvances(
  params: { status?: string } = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery<AdvanceRow[]>({
    queryKey: ["advances", "list", params],
    queryFn: () =>
      api
        .get<AdvanceRow[]>("/payroll/advances", {
          params: { status: params.status || undefined },
        })
        .then((r) => r.data),
    enabled: opts.enabled ?? true,
  });
}

export function useMyAdvances() {
  return useQuery<MyAdvancesResponse>({
    queryKey: ["advances", "mine"],
    queryFn: () => api.get<MyAdvancesResponse>("/payroll/me/advances").then((r) => r.data),
  });
}

export function useRequestAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { amount: number; reason: string; installments: number }) =>
      api.post<AdvanceRow>("/payroll/advances", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

export function useAdvanceAction(action: "approve-hr" | "cancel") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (advanceId: string) =>
      api.post<AdvanceRow>(`/payroll/advances/${advanceId}/${action}`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

/** Final approval also captures the settlement reference (voucher / txn no.),
 *  the payout mode, and the source pool the money came from — mandatory on
 *  every mode and hard-blocked against the pool balance server-side. */
export function useFinalApproveAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      advanceId: string;
      settlementRef: string;
      settlementNote?: string;
      settlementMode: "bank" | "cash" | "upi" | "cheque";
      settlementPoolId: string;
      installments?: number | null;
    }) =>
      api
        .post<AdvanceRow>(`/payroll/advances/${input.advanceId}/approve-final`, {
          settlementRef: input.settlementRef,
          settlementNote: input.settlementNote ?? "",
          settlementMode: input.settlementMode,
          settlementPoolId: input.settlementPoolId,
          installments: input.installments ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["advances"] });
      qc.invalidateQueries({ queryKey: ["cashPools"] });
    },
  });
}

/** HR records a cash/bank repayment made outside the payroll run. */
export function useRecordAdvanceRepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      advanceId: string;
      amount: number;
      reference?: string;
      note?: string;
    }) =>
      api
        .post<AdvanceRow>(`/payroll/advances/${input.advanceId}/repayments`, {
          amount: input.amount,
          reference: input.reference ?? "",
          note: input.note ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

export function useRejectAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { advanceId: string; note: string }) =>
      api
        .post<AdvanceRow>(`/payroll/advances/${input.advanceId}/reject`, { note: input.note })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["advances"] }),
  });
}

/* ----------------------------- reimbursements ----------------------------- */

export type ClaimStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "paid"
  | "revoked";
export type StepState = "pending" | "approved" | "rejected" | "skipped" | "overridden";

export interface ClaimStep {
  order: number;
  kind: "requester_dept_head" | "specific_dept_head" | "role";
  departmentName: string | null;
  role: string | null;
  minAmount: string | null;
  approverName: string | null;
  state: StepState;
  skippedReason: string | null;
  actedByName: string | null;
  actedAt: string | null;
  note: string;
}

export interface ClaimRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  categoryLabel: string;
  workUnitId: string;
  workUnitCtrlNo: string;
  amount: string;
  note: string;
  receiptId: string;
  status: ClaimStatus;
  resolutionNote: string;
  currentOrder: number | null;
  steps: ClaimStep[];
  createdAt: string | null;
  revokedByName: string | null;
  revokedAt: string | null;
  revocationReason: string;
  restoredByName: string | null;
  restoredAt: string | null;
  restorationReason: string;
  canAct?: boolean;
  actIsOverride?: boolean;
  /** SA/TA only — revoke an approved claim / restore a rejected+revoked one. */
  canRevoke?: boolean;
  canRestore?: boolean;
}

export interface ClaimCategory {
  id: string;
  label: string;
  status: "active" | "inactive";
}

export interface ChainStepConfig {
  order?: number;
  kind: "requester_dept_head" | "specific_dept_head" | "role";
  departmentId: string | null;
  departmentName?: string | null;
  role: string | null;
  minAmount: string | null;
}

export function useClaims(params: { status?: string } = {}) {
  return useQuery<ClaimRow[]>({
    queryKey: ["claims", "list", params],
    queryFn: () =>
      api
        .get<ClaimRow[]>("/payroll/reimbursements", {
          params: { status: params.status || undefined },
        })
        .then((r) => r.data),
  });
}

export function useMyClaims() {
  return useQuery<ClaimRow[]>({
    queryKey: ["claims", "mine"],
    queryFn: () => api.get<ClaimRow[]>("/payroll/me/reimbursements").then((r) => r.data),
  });
}

export function useMyClaimApprovals(enabled = true) {
  return useQuery<ClaimRow[]>({
    queryKey: ["claims", "approvals"],
    queryFn: () =>
      api.get<ClaimRow[]>("/payroll/me/reimbursement-approvals").then((r) => r.data),
    enabled,
  });
}

export function useSubmitClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      categoryId: string;
      workUnitId: string;
      amount: number;
      note?: string;
      receiptId: string;
    }) => api.post<ClaimRow>("/payroll/reimbursements", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useActOnClaimStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      claimId: string;
      order: number;
      decision: "approve" | "reject";
      note?: string;
    }) =>
      api
        .post<ClaimRow>(
          `/payroll/reimbursements/${input.claimId}/steps/${input.order}/${input.decision}`,
          { note: input.note ?? "" },
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useCancelClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (claimId: string) =>
      api.post<ClaimRow>(`/payroll/reimbursements/${claimId}/cancel`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

/* --------------------- claim reversal (SA/TA only) --------------------- */

export interface ClaimReversalPreview {
  reimbursementId: string;
  empId: string;
  fullName: string;
  categoryLabel: string;
  amount: string;
  status: ClaimStatus;
  payroll: ReversalPayrollState;
}

export function useRevokeClaimPreview(claimId: string | undefined, open: boolean) {
  return useQuery<ClaimReversalPreview>({
    queryKey: ["claims", claimId ?? "_", "revokePreview"],
    queryFn: () =>
      api
        .get<ClaimReversalPreview>(`/payroll/reimbursements/${claimId}/revoke`)
        .then((r) => r.data),
    enabled: open && !!claimId,
    staleTime: 0,
    retry: false,
  });
}

export function useRevokeClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { claimId: string; reason: string }) =>
      api
        .post<ClaimRow>(`/payroll/reimbursements/${input.claimId}/revoke`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useRestoreClaimPreview(claimId: string | undefined, open: boolean) {
  return useQuery<ClaimReversalPreview>({
    queryKey: ["claims", claimId ?? "_", "restorePreview"],
    queryFn: () =>
      api
        .get<ClaimReversalPreview>(`/payroll/reimbursements/${claimId}/restore`)
        .then((r) => r.data),
    enabled: open && !!claimId,
    staleTime: 0,
    retry: false,
  });
}

export function useRestoreClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { claimId: string; reason: string }) =>
      api
        .post<ClaimRow>(`/payroll/reimbursements/${input.claimId}/restore`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export function useClaimCategories(status: "active" | "all" = "active") {
  return useQuery<ClaimCategory[]>({
    queryKey: ["claimCategories", status],
    queryFn: () =>
      api
        .get<ClaimCategory[]>("/payroll/reimbursement-categories", { params: { status } })
        .then((r) => r.data),
  });
}

export function useCreateClaimCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string }) =>
      api.post<ClaimCategory>("/payroll/reimbursement-categories", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claimCategories"] }),
  });
}

export function useSetClaimCategoryStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; action: "activate" | "deactivate" }) =>
      api
        .post<ClaimCategory>(
          `/payroll/reimbursement-categories/${input.id}/${input.action}`,
          {},
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claimCategories"] }),
  });
}

export function useApprovalChain() {
  return useQuery<{ steps: ChainStepConfig[]; isDefault: boolean }>({
    queryKey: ["approvalChain"],
    queryFn: () =>
      api
        .get<{ steps: ChainStepConfig[]; isDefault: boolean }>("/payroll/approval-chain")
        .then((r) => r.data),
  });
}

export function useUpdateApprovalChain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { steps: ChainStepConfig[] }) =>
      api
        .put<{ steps: ChainStepConfig[]; isDefault: boolean }>("/payroll/approval-chain", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvalChain"] }),
  });
}

/* --------------------------- vouchers + disbursal --------------------------- */

export interface VoucherItemRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  label: string;
  voucherNo: string;
  amount: string;
  note: string;
  attachmentId: string | null;
  createdAt: string | null;
}

export interface DisbursalAllocationRow {
  id: string;
  // Null when the employee has no line in the current compute (identity is
  // employee-keyed; the line pointer re-attaches after recompute).
  lineId: string | null;
  empId: string;
  fullName: string;
  amount: string;
}

export interface DisbursalBatchRow {
  id: string;
  disbursedOn: string;
  mode: "bank" | "cash" | "upi" | "cheque";
  // Accounting voucher number (books entry). Mandatory on create; blank only
  // on batches recorded before 2026-07-18.
  voucherNo: string;
  reference: string;
  note: string;
  voucherId: string | null;
  /** Pool the money was paid from — set on every batch since 2026-09-08;
   *  null only on rows recorded before the pool feature (legacy,
   *  backfillable). */
  sourcePoolId: string | null;
  sourcePoolMonth: string | null;
  employeeCount: number;
  totalAmount: string;
  allocations: DisbursalAllocationRow[];
  isVoided: boolean;
  voidReason: string;
  voidedAt: string | null;
  voidedBy: string | null;
  createdAt: string | null;
}

export interface DisbursalAllocationInput {
  lineId: string;
  amount: number;
}

export function useRunVouchers(runId: string | undefined) {
  return useQuery<VoucherItemRow[]>({
    queryKey: ["payrollRuns", runId ?? "_", "vouchers"],
    queryFn: () =>
      api.get<VoucherItemRow[]>(`/payroll/runs/${runId}/vouchers`).then((r) => r.data),
    enabled: !!runId,
  });
}

export function useAddVoucherItem(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      employeeId: string;
      label: string;
      voucherNo?: string;
      amount: number;
      note?: string;
      attachmentId?: string | null;
    }) =>
      api.post<VoucherItemRow>(`/payroll/runs/${runId}/vouchers`, input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useRemoveVoucherItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voucherId: string) =>
      api.delete(`/payroll/vouchers/${voucherId}`).then(() => true),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollRuns"] }),
  });
}

export function useRunDisbursalBatches(runId: string | undefined) {
  return useQuery<DisbursalBatchRow[]>({
    queryKey: ["payrollRuns", runId ?? "_", "disbursalBatches"],
    queryFn: () =>
      api
        .get<DisbursalBatchRow[]>(`/payroll/runs/${runId}/disbursal-batches`)
        .then((r) => r.data),
    enabled: !!runId,
  });
}

export function useCreateDisbursalBatch(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      allocations: DisbursalAllocationInput[];
      disbursedOn: string;
      mode: DisbursalBatchRow["mode"];
      voucherNo: string;
      reference?: string;
      note?: string;
      voucherId?: string | null;
      /** Mandatory on every mode — the server hard-blocks against the balance. */
      sourcePoolId: string;
    }) =>
      api
        .post<DisbursalBatchRow>(`/payroll/runs/${runId}/disbursal-batches`, input)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payrollRuns"] });
      qc.invalidateQueries({ queryKey: ["carryForwards"] });
      qc.invalidateQueries({ queryKey: ["cashPools"] });
    },
  });
}

export function useVoidDisbursalBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { batchId: string; reason: string }) =>
      api
        .post<DisbursalBatchRow>(`/payroll/disbursal-batches/${input.batchId}/void`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payrollRuns"] });
      qc.invalidateQueries({ queryKey: ["carryForwards"] });
      qc.invalidateQueries({ queryKey: ["cashPools"] });
    },
  });
}

/* --------------------------- carry-forwards ----------------------------- */

export type CarryForwardState = "open" | "recovered" | "waived" | "closed";

export interface CarryForwardLedgerEntry {
  id: string;
  kind: "payroll" | "manual";
  amount: string;
  runMonth: string | null;
  reference: string;
  note: string;
  recordedAt: string | null;
  balanceAfter: string;
}

export interface CarryForwardRow {
  id: string;
  employeeId: string;
  empId: string;
  fullName: string;
  sourceRunId: string;
  sourceMonth: string; // ISO date, first of month
  amount: string;
  recoveredAmount: string;
  openAmount: string;
  /** Recovered beyond the (void-shrunk) excess — company owes this back. */
  overRecoveredAmount: string;
  state: CarryForwardState;
  waivedAt: string | null;
  waivedBy: string | null;
  waiveReason: string;
  createdAt: string | null;
  ledger: CarryForwardLedgerEntry[];
}

export function useCarryForwards(
  params: { state?: string; employeeId?: string } = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery<CarryForwardRow[]>({
    queryKey: ["carryForwards", "list", params],
    queryFn: () =>
      api
        .get<CarryForwardRow[]>("/payroll/carry-forwards", {
          params: {
            state: params.state || undefined,
            employeeId: params.employeeId || undefined,
          },
        })
        .then((r) => r.data),
    enabled: opts.enabled ?? true,
  });
}

/** One employee's cash-excess position relative to a single run. */
export interface RunCarryForwardRow {
  employeeId: string;
  empId: string;
  fullName: string;
  /** Null when the excess belongs to someone with no line this month. */
  lineId: string | null;
  net: string | null;
  /** Open excess from earlier finalized months this run may claw back. */
  recoverable: string;
  /** What the last compute wrote onto the line. */
  planned: string;
  /** What a recompute right now would plan — differs from `planned` when stale. */
  wouldPlan: string;
  /** Ledger rows this run committed at finalize. */
  committed: string;
  /** Excess this run itself created by being overpaid. */
  sourcedHere: string;
  sourceMonths: string[];
  state: "committed" | "planned" | "stale" | "none" | "no_line";
  stale: boolean;
}

export interface RunCarryForwardContext {
  runId: string;
  month: string;
  status: RunStatus;
  rows: RunCarryForwardRow[];
  totals: {
    recoverable: string;
    planned: string;
    committed: string;
    sourcedHere: string;
    staleCount: number;
    staleAmount: string;
  };
}

export function useRunCarryForwards(runId: string | undefined) {
  return useQuery<RunCarryForwardContext>({
    queryKey: ["payrollRuns", runId, "carryForwards"],
    queryFn: () =>
      api.get<RunCarryForwardContext>(`/payroll/runs/${runId}/carry-forwards`).then((r) => r.data),
    enabled: !!runId,
  });
}

export function useRecordCarryForwardReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { cfId: string; amount: number; reference?: string; note?: string }) =>
      api
        .post<CarryForwardRow>(`/payroll/carry-forwards/${input.cfId}/returns`, {
          amount: input.amount,
          reference: input.reference || undefined,
          note: input.note || undefined,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["carryForwards"] }),
  });
}

export function useWaiveCarryForward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { cfId: string; reason: string }) =>
      api
        .post<CarryForwardRow>(`/payroll/carry-forwards/${input.cfId}/waive`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["carryForwards"] }),
  });
}

/* ----------------------------- cash pools -------------------------------- */

export type CashPoolState = "open" | "closed";

export interface CashReceiptRow {
  id: string;
  amount: string;
  receivedOn: string;
  mode: "bank" | "cash" | "upi" | "cheque";
  voucherNo: string;
  receivedFrom: string;
  note: string;
  attachmentId: string | null;
  isVoided: boolean;
  voidReason: string;
  voidedAt: string | null;
  voidedBy: string | null;
  createdAt: string | null;
}

export interface CashPoolReturnRow {
  id: string;
  amount: string;
  returnedOn: string;
  voucherNo: string;
  note: string;
  attachmentId: string | null;
  isVoided: boolean;
  voidReason: string;
  createdAt: string | null;
}

export interface CashPoolOutflowEntry {
  empId: string;
  fullName: string;
  amount: string;
}

export interface CashPoolOutflowRow {
  kind: "salary" | "advance";
  batchId: string | null;
  runMonth: string | null;
  date: string | null;
  mode: "bank" | "cash" | "upi" | "cheque" | "";
  voucherNo: string;
  reference: string;
  amount: string;
  /** Employee name on advance settlements; null on salary batches. */
  label: string | null;
  /** Who actually got the money inside this books entry. */
  entries: CashPoolOutflowEntry[];
}

export interface CashPoolRow {
  id: string;
  month: string; // ISO date, first of month
  state: CashPoolState;
  receivedTotal: string;
  disbursedTotal: string;
  returnedTotal: string;
  balance: string;
  closedAt: string | null;
  closedBy: string | null;
  reopenedAt: string | null;
  reopenedBy: string | null;
  reopenReason: string;
  createdAt: string | null;
  receipts?: CashReceiptRow[];
  returns?: CashPoolReturnRow[];
  outflows?: CashPoolOutflowRow[];
}

export function useCashPools(
  params: { state?: CashPoolState | "" } = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery<CashPoolRow[]>({
    queryKey: ["cashPools", "list", params],
    queryFn: () =>
      api
        .get<CashPoolRow[]>("/payroll/cash-pools", {
          params: { state: params.state || undefined },
        })
        .then((r) => r.data),
    enabled: opts.enabled ?? true,
  });
}

export function useCashPool(poolId: string | undefined) {
  return useQuery<CashPoolRow>({
    queryKey: ["cashPools", poolId ?? "_"],
    queryFn: () => api.get<CashPoolRow>(`/payroll/cash-pools/${poolId}`).then((r) => r.data),
    enabled: !!poolId,
  });
}

/** Records money received from the head — auto-creates the month's pool. */
export function useRecordCashReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      month: string; // any date in the month
      amount: number;
      receivedOn: string;
      mode: CashReceiptRow["mode"];
      voucherNo: string;
      receivedFrom?: string;
      note?: string;
      attachmentId?: string | null;
    }) =>
      api
        .post<CashPoolRow>("/payroll/cash-pools", {
          ...input,
          receivedFrom: input.receivedFrom ?? "",
          note: input.note ?? "",
          attachmentId: input.attachmentId ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashPools"] }),
  });
}

export function useVoidCashReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { receiptId: string; reason: string }) =>
      api
        .post<CashPoolRow>(`/payroll/cash-receipts/${input.receiptId}/void`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashPools"] }),
  });
}

/** Close = return the exact remaining balance to the head. Voucher number is
 *  mandatory unless the balance is zero (nothing physically handed back). */
export function useCloseCashPool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      poolId: string;
      voucherNo?: string;
      returnedOn?: string | null;
      note?: string;
      attachmentId?: string | null;
    }) =>
      api
        .post<CashPoolRow>(`/payroll/cash-pools/${input.poolId}/close`, {
          voucherNo: input.voucherNo ?? "",
          returnedOn: input.returnedOn ?? null,
          note: input.note ?? "",
          attachmentId: input.attachmentId ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashPools"] }),
  });
}

/** SA/TA only — soft-voids the closing return; pool must be re-closed later. */
export function useReopenCashPool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { poolId: string; reason: string }) =>
      api
        .post<CashPoolRow>(`/payroll/cash-pools/${input.poolId}/reopen`, {
          reason: input.reason,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashPools"] }),
  });
}

/** Backfill — every sourceless batch on the run adopts the pool (any mode). */
export function useAssignRunCashPool(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { poolId: string }) =>
      api
        .post<{ pool: CashPoolRow; batches: number; total: string }>(
          `/payroll/runs/${runId}/assign-cash-pool`,
          { poolId: input.poolId },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashPools"] });
      qc.invalidateQueries({ queryKey: ["payrollRuns"] });
    },
  });
}

export function slipPreviewUrl(profileId?: string): string {
  const base = api.defaults.baseURL ?? "";
  const token = useAuthStore.getState().accessToken;
  const params = new URLSearchParams();
  if (token) params.set("token", token);
  if (profileId) params.set("profile", profileId);
  const qs = params.toString();
  return `${base}/payroll/slip-preview${qs ? `?${qs}` : ""}`;
}

/* --------------------------- branding profiles --------------------------- */

/** Image value shape shared with the server: null, a plain attachment id, or
 *  {id, width?, height?, offsetX?, offsetY?} in PDF points. */
export type SlipImageValue =
  | string
  | null
  | {
      id: string | null;
      size?: "sm" | "md" | "lg";
      width?: number | null;
      height?: number | null;
      offsetX?: number | null;
      offsetY?: number | null;
    };

export interface BrandingCompanyBlock {
  name?: string;
  address?: string;
  email?: string;
  signatory?: string;
  verifyNote?: string;
  showNameWithLogo?: boolean;
}

export interface BrandingProfileRow {
  id: string;
  name: string;
  isDefault: boolean;
  status: "active" | "inactive";
  logo: SlipImageValue;
  signature: SlipImageValue;
  stamp: SlipImageValue;
  companyBlock: BrandingCompanyBlock;
  /** Active employees explicitly assigned to this profile. */
  employeeCount?: number;
  /** Default profile only: active employees with no assignment — they print
   *  with the default via fallback. */
  fallbackCount?: number;
}

export interface BrandingProfilePatch {
  name?: string;
  logo?: SlipImageValue;
  signature?: SlipImageValue;
  stamp?: SlipImageValue;
  companyBlock?: BrandingCompanyBlock;
}

export function useBrandingProfiles() {
  return useQuery<BrandingProfileRow[]>({
    queryKey: ["brandingProfiles"],
    queryFn: () =>
      api.get<BrandingProfileRow[]>("/payroll/branding-profiles").then((r) => r.data),
  });
}

export function useCreateBrandingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api.post<BrandingProfileRow>("/payroll/branding-profiles", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brandingProfiles"] }),
  });
}

export function useUpdateBrandingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { profileId: string; patch: BrandingProfilePatch }) =>
      api
        .patch<BrandingProfileRow>(`/payroll/branding-profiles/${input.profileId}`, input.patch)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brandingProfiles"] }),
  });
}

export function useSetBrandingProfileStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { profileId: string; action: "activate" | "deactivate" }) =>
      api
        .post<BrandingProfileRow>(
          `/payroll/branding-profiles/${input.profileId}/${input.action}`,
          {},
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brandingProfiles"] }),
  });
}

export function useAssignBrandingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeIds: string[]; profileId: string | null }) =>
      api
        .post<{ updated: number }>("/payroll/branding-assign", input)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payrollEmployees"] });
      qc.invalidateQueries({ queryKey: ["brandingProfiles"] });
    },
  });
}

export function usePayrollSettings() {
  return useQuery<PayrollSettingsResponse>({
    queryKey: ["payrollSettings"],
    queryFn: () => api.get<PayrollSettingsResponse>("/payroll/settings").then((r) => r.data),
  });
}

export function useUpdatePayrollSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; value: unknown }) =>
      api.patch<PayrollSettingsResponse>("/payroll/settings", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payrollSettings"] }),
  });
}
